import { requestConfirmation } from "../lib/confirmation";
import { Controller } from "@hotwired/stimulus"

// Breathing room between a capped menu and the edge of the list it sits in, and
// the floor below which capping it further would show nothing useful.
const MENU_GAP = 8
const MIN_MENU_HEIGHT = 96

// Picks which household member an ordered line is for. Every member's label
// chip is rendered up front and toggled on selection, so avatars stay
// server-rendered rather than being rebuilt in JS.
export default class extends Controller {
  static targets = ["input", "option", "menu", "override"]

  declare inputTarget: HTMLInputElement
  declare optionTargets: HTMLElement[]
  declare menuTarget: HTMLElement
  declare hasMenuTarget: boolean
  declare overrideTarget: HTMLInputElement
  declare hasOverrideTarget: boolean

  // The item list is a fixed-height scroll container, so a menu opening
  // downward from a lower row gets clipped. Flip it above the trigger when
  // there is no room below but there is above.
  //
  // The container is marked explicitly rather than found via closest("turbo-frame"):
  // every row is itself a turbo-frame, so that would measure the row.
  open() {
    if (!this.hasMenuTarget) return

    const scroller = this.element.closest("[data-recipient-scroll]")
    if (!scroller) return

    requestAnimationFrame(() => {
      // Cleared before measuring: a cap left over from the last open would be
      // read back as the menu's natural height.
      this.menuTarget.style.maxHeight = ""
      this.menuTarget.style.overflowY = ""

      const trigger = this.element.getBoundingClientRect()
      const bounds = scroller.getBoundingClientRect()
      const height = this.menuTarget.offsetHeight
      const spaceBelow = bounds.bottom - trigger.bottom
      const spaceAbove = trigger.top - bounds.top
      const fitsBelow = height <= spaceBelow

      // A tall menu in a short list fits on neither side — open toward whichever
      // side clips least rather than always falling through to downward.
      const flip = !fitsBelow && (height <= spaceAbove || spaceAbove > spaceBelow)

      this.element.classList.toggle("dropdown-top", flip)

      // Whichever side it opens toward may still be too short — a four-person
      // household needs ~226px and a phone's list pane offers less. Cap the menu
      // to the room it has and let it scroll, or the first option is clipped out
      // of reach with no way to get at it.
      const available = flip ? spaceAbove : spaceBelow
      if (height <= available) return

      this.menuTarget.style.maxHeight = `${Math.max(available - MENU_GAP, MIN_MENU_HEIGHT)}px`
      this.menuTarget.style.overflowY = "auto"
    })
  }

  async select(event: Event) {
    event.preventDefault()

    const button = event.currentTarget as HTMLElement
    const id = button.dataset.recipientId
    if (!id || id === this.inputTarget.value) return this.close()

    // Bypassing a cooldown asks first, matching the Override affordance on the
    // item panel. Only rendered for someone who can actually override.
    const confirmMessage = button.dataset.cooldownConfirm
    if (confirmMessage && !await requestConfirmation(confirmMessage)) return this.close()

    this.inputTarget.value = id
    this.showLabelFor(id)

    // Carried on the request the auto-save is about to make: the server refuses
    // a blocked recipient unless the override was actually asked for. Cleared
    // when moving back to an unblocked member so it cannot ride along.
    const blocked = button.dataset.blocked === "true"
    if (this.hasOverrideTarget) this.overrideTarget.value = blocked ? "1" : ""

    // Tell the row so it can carry the override warning without waiting for a
    // re-render.
    if (blocked) this.dispatch("override", { bubbles: true })

    // Let the auto-save controller on the surrounding form persist the change.
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.close()
  }

  private showLabelFor(id: string) {
    this.optionTargets.forEach((option) => {
      const matches = option.dataset.recipientId === id
      option.classList.toggle("hidden", !matches)
      option.classList.toggle("flex", matches)
    })
  }

  // DaisyUI dropdowns stay open until the trigger loses focus.
  private close() {
    ;(document.activeElement as HTMLElement | null)?.blur()
  }
}
